/**
 * Studio home — PURE helpers behind the Content Engine's decluttered flow.
 *
 * The operator called the old view "so busy, not intuitive": every tool
 * (SEO editor, multi-format generation, grounded generation, performance
 * ledger, weekly loop, governed calendar, sales composer…) rendered on one
 * endless page. This module encodes the fix as data + pure functions:
 *
 *   • TYPE_TOOLKITS       — which advanced tools belong to WHICH content
 *                           type, so the workspace only shows relevant ones;
 *   • toolsForType/hasTool — lookups the view gates panels with;
 *   • DEFAULT_GROUNDED_FORMAT — the grounded-generation tab a type opens on;
 *   • upcomingScheduleDays — the simple next-N-days scheduled-posts strip;
 *   • analyticsTiles       — the home analytics strip (counts by status,
 *                           honest average score, upcoming count);
 *   • IMAGE_GEN_ENDPOINT + buildImagePrompt — the honest image-generation
 *                           affordance (backend wired separately).
 *
 * PURE by contract: no store, no network, no clock — callers pass `nowMs`.
 */

import type { ContentItem, ContentItemStatus, ContentItemType } from '../db/types';
import type { ContentAnalytics } from './pipeline';
import { CONTENT_STATUS_LABELS, CONTENT_STATUSES } from './pipeline';

// ---------------------------------------------------------------------------
// Workspace toolkits — which dense tools each content type actually needs.
// ---------------------------------------------------------------------------

/** Every advanced panel the workspace can mount, as a stable id. */
export type WorkspaceTool =
  | 'seo-editor'
  | 'multi-format'
  | 'grounded'
  | 'history'
  | 'learning-loop'
  | 'performance'
  | 'governed-calendar'
  | 'sales-composer'
  | 'image-gen';

export interface WorkspaceToolMeta {
  id: WorkspaceTool;
  label: string;
  description: string;
}

/** Human labels for the workspace's tool list. */
export const WORKSPACE_TOOL_META: Record<WorkspaceTool, WorkspaceToolMeta> = {
  'seo-editor': {
    id: 'seo-editor',
    label: 'Live SEO editor',
    description: 'Score-as-you-type SEO editor with keyword chips + SERP preview.',
  },
  'multi-format': {
    id: 'multi-format',
    label: 'Multi-format generation',
    description: 'One brief → four platform formats, each auto-scored.',
  },
  grounded: {
    id: 'grounded',
    label: 'Grounded generation',
    description: 'Per-format constraints, CRM facts, provenance + validation.',
  },
  history: {
    id: 'history',
    label: 'History-shaped drafts',
    description: 'Prompts blended from YOUR what-works signals + playbook rules.',
  },
  'learning-loop': {
    id: 'learning-loop',
    label: 'Weekly learning loop',
    description: 'Governed weekly ritual: signals → profile update → verify.',
  },
  performance: {
    id: 'performance',
    label: 'Post performance',
    description: 'Log real metrics → derive per-author signals honestly.',
  },
  'governed-calendar': {
    id: 'governed-calendar',
    label: 'Governed calendar',
    description: 'Approvable, sealed, reversible scheduling of assets.',
  },
  'sales-composer': {
    id: 'sales-composer',
    label: 'Sales composer',
    description: 'Deterministic persona/target-personalized outreach drafts.',
  },
  'image-gen': {
    id: 'image-gen',
    label: 'Generate image',
    description: 'Companion visual via the /generate-image endpoint.',
  },
};

/**
 * The toolkit each content type opens with — the core of the declutter:
 * a blog never shows the post-performance ledger, an email never shows the
 * SEO editor. Order = render order in the workspace.
 */
export const TYPE_TOOLKITS: Record<ContentItemType, readonly WorkspaceTool[]> = {
  'linkedin-post': [
    'grounded',
    'multi-format',
    'history',
    'learning-loop',
    'performance',
    'governed-calendar',
    'sales-composer',
    'image-gen',
  ],
  'x-thread': [
    'grounded',
    'multi-format',
    'history',
    'learning-loop',
    'performance',
    'governed-calendar',
    'image-gen',
  ],
  // Single X post — same X-native toolkit as the thread workspace.
  'x-post': [
    'grounded',
    'multi-format',
    'history',
    'learning-loop',
    'performance',
    'governed-calendar',
    'image-gen',
  ],
  blog: ['seo-editor', 'governed-calendar', 'image-gen'],
  email: ['grounded', 'multi-format', 'sales-composer', 'governed-calendar'],
  'ig-caption': ['performance', 'governed-calendar', 'image-gen'],
};

/** Tools for one type, in workspace render order. */
export function toolsForType(type: ContentItemType): WorkspaceTool[] {
  return [...(TYPE_TOOLKITS[type] ?? [])];
}

/** Does `type`'s workspace mount `tool`? */
export function hasTool(type: ContentItemType, tool: WorkspaceTool): boolean {
  return (TYPE_TOOLKITS[type] ?? []).includes(tool);
}

/**
 * Grounded-generation format tab a type's workspace opens on — keys into
 * generate.ts's FORMAT_SPECS. Every content type maps to its closest spec
 * so the operator lands on the right constraints, not a default.
 */
export const DEFAULT_GROUNDED_FORMAT: Record<ContentItemType, string> = {
  'linkedin-post': 'linkedinPost',
  'x-thread': 'xThread',
  // Closest X-native spec until a dedicated single-post spec ships.
  'x-post': 'xThread',
  blog: 'blogPost',
  email: 'coldEmail',
  'ig-caption': 'linkedinPost',
};

// ---------------------------------------------------------------------------
// Scheduled-posts strip — the simple next-N-days calendar on the home.
// ---------------------------------------------------------------------------

/** One column of the home calendar strip. */
export interface ScheduleDay {
  /** UTC day key, 'YYYY-MM-DD'. */
  key: string;
  /** Short human label, e.g. 'Mon 12'. */
  label: string;
  isToday: boolean;
  /** Scheduled items slotted on this UTC day, sorted by time ascending. */
  items: ContentItem[];
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const DAY_MS = 24 * 60 * 60 * 1000;

/** UTC 'YYYY-MM-DD' key for an ms epoch. */
export function utcDayKey(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Build the next `days` UTC days starting at `nowMs`'s day, slotting every
 * `scheduled` item whose `scheduledFor` parses into that day. Days with no
 * posts are still present (a calendar has empty days). Items missing or
 * carrying an unparseable date are skipped — never guessed. Pure.
 */
export function upcomingScheduleDays(
  items: ContentItem[],
  nowMs: number,
  days = 7,
): ScheduleDay[] {
  const startKey = utcDayKey(nowMs);
  const byDay = new Map<string, ContentItem[]>();
  for (const item of items) {
    if (item.status !== 'scheduled' || !item.scheduledFor) continue;
    const t = Date.parse(item.scheduledFor);
    if (!Number.isFinite(t)) continue;
    const key = utcDayKey(t);
    if (key < startKey) continue; // past — not "upcoming"
    (byDay.get(key) ?? byDay.set(key, []).get(key)!).push(item);
  }

  const out: ScheduleDay[] = [];
  const d0 = new Date(nowMs);
  const dayStart = Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth(), d0.getUTCDate());
  for (let i = 0; i < days; i += 1) {
    const dayMs = dayStart + i * DAY_MS;
    const key = utcDayKey(dayMs);
    const d = new Date(dayMs);
    const slotted = [...(byDay.get(key) ?? [])].sort((a, b) =>
      (a.scheduledFor ?? '') < (b.scheduledFor ?? '') ? -1 : 1,
    );
    out.push({
      key,
      label: `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()}`,
      isToday: i === 0,
      items: slotted,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Analytics strip — the home's honest at-a-glance numbers.
// ---------------------------------------------------------------------------

/** One tile of the home analytics strip. */
export interface AnalyticsTile {
  key: string;
  label: string;
  value: string;
  /** Optional honesty note (e.g. why avg score is a dash). */
  hint?: string;
}

/**
 * Turn a pipeline summary into display tiles: total, one per status, the
 * honest average score ('—' with a hint when nothing is scored — never a
 * fabricated 0), and the upcoming-scheduled count. Pure formatting only;
 * all math lives in pipeline.summarizeContent.
 */
export function analyticsTiles(a: ContentAnalytics): AnalyticsTile[] {
  const tiles: AnalyticsTile[] = [
    { key: 'total', label: 'Pieces', value: String(a.total) },
  ];
  for (const status of CONTENT_STATUSES as readonly ContentItemStatus[]) {
    tiles.push({
      key: status,
      label: CONTENT_STATUS_LABELS[status],
      value: String(a.byStatus[status]),
    });
  }
  tiles.push(
    a.avgScore === null
      ? {
          key: 'avg-score',
          label: 'Avg score',
          value: '—',
          hint: 'No scored drafts yet — save a body to score it.',
        }
      : {
          key: 'avg-score',
          label: 'Avg score',
          value: `${a.avgScore}/100`,
          hint: `Average of ${a.scoredCount} scored draft${a.scoredCount === 1 ? '' : 's'}.`,
        },
  );
  tiles.push({ key: 'upcoming', label: 'Upcoming', value: String(a.upcomingScheduled) });
  return tiles;
}

// ---------------------------------------------------------------------------
// Image generation — honest affordance for a backend wired separately.
// ---------------------------------------------------------------------------

/** The endpoint the Generate-image button POSTs to. Backend wired by the
 *  operator separately — the UI says so plainly until it responds. */
export const IMAGE_GEN_ENDPOINT = '/generate-image';

/** Per-type art direction so a prompt lands the right canvas + mood. */
const IMAGE_ART_DIRECTION: Record<ContentItemType, string> = {
  'linkedin-post': 'landscape 1200x627, clean editorial illustration, professional tone',
  'x-thread': 'landscape 1600x900, bold high-contrast graphic, one focal idea',
  'x-post': 'landscape 1600x900, single bold statement graphic, high contrast',
  blog: 'wide 1920x1080 hero image, article header, generous negative space',
  email: 'compact 600px-wide banner, simple flat illustration, fast-loading',
  'ig-caption': 'square 1080x1080, vivid photographic style, thumb-stopping',
};

/**
 * Deterministic image prompt for a piece: the title (or an honest fallback)
 * plus per-type art direction. Pure string building — the POST happens in
 * the view.
 */
export function buildImagePrompt(type: ContentItemType, title: string): string {
  const subject = title.trim() || 'the piece being drafted';
  return `Illustration for "${subject}" — ${IMAGE_ART_DIRECTION[type]}. No embedded text.`;
}
